import React, { useEffect, useMemo, useState } from 'react';
import {
  AccountBalance,
  Badge,
  CreditCard,
  CreditScore,
  Percent,
} from '@mui/icons-material';
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoCompleteBank from '../../components/AutoCompleteBank';
import RadioButton from '../../components/RadioButton';
import API from '../../services/api';
import { BANK_OPTIONS, mergeBankOptions } from '../../constants/banks';

export default function SupplierAbastoConfig({ formData = {}, onChange }) {
  const [bankOptions, setBankOptions] = useState(BANK_OPTIONS);

  useEffect(() => {
    let mounted = true;
    API.get('bank-account/')
      .then(({ data }) => {
        if (!mounted) return;
        const accounts = Array.isArray(data) ? data : data?.results || [];
        setBankOptions(mergeBankOptions(accounts));
      })
      .catch(() => {
        if (mounted) setBankOptions(BANK_OPTIONS);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedBank = useMemo(() => (
    bankOptions.find((bank) => bank.name === formData.abasto_bank_name) || null
  ), [bankOptions, formData.abasto_bank_name]);

  const handleBankChange = (name, value) => {
    onChange?.('abasto_bank_name', value?.name || '');
  };

  const handleTextChange = (name) => (event) => {
    onChange?.(name, event.target.value);
  };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AccountBalance sx={{ color: '#10173c', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Ficha de Abasto
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(160px, 1fr))' },
          gap: 1,
          alignItems: 'center',
        }}
      >
        <AutoCompleteBank
          options={bankOptions}
          getOptionLabel={(option) => option?.name || ''}
          onChange={handleBankChange}
          name="abasto_bank_name"
          value={selectedBank}
          label="Banco"
          marginBottom="0"
          sx={{ width: '100%', p: 0 }}
        />

        <TextField
          label="Nombre del convenio"
          size="small"
          value={formData.abasto_agreement_name || ''}
          onChange={handleTextChange('abasto_agreement_name')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Badge fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Numero de convenio"
          size="small"
          value={formData.abasto_agreement_number || ''}
          onChange={handleTextChange('abasto_agreement_number')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CreditCard fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Tasa"
          type="number"
          size="small"
          value={formData.abasto_interest_rate ?? ''}
          onChange={handleTextChange('abasto_interest_rate')}
          inputProps={{ min: 0, max: 100, step: '0.01' }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
            startAdornment: (
              <InputAdornment position="start">
                <Percent fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <RadioButton
          name="isCredit"
          initialValue={formData.isCredit ?? true}
          onChange={onChange}
          renderCreditFields={() => (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(180px, 240px))' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                label="Monto asignado por el banco"
                type="number"
                size="small"
                value={formData.creditLimit ?? ''}
                onChange={handleTextChange('creditLimit')}
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditScore fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Plazo en dias"
                type="number"
                size="small"
                value={formData.creditPeriod ?? ''}
                onChange={handleTextChange('creditPeriod')}
                inputProps={{ min: 0, step: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditCard fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        />
      </Box>
    </Box>
  );
}
